import React, { useState, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, XIcon, AlertCircleIcon, TrashIcon } from 'lucide-react';
interface EditRoomFormProps {
  roomData: any;
  onSave: (roomData: any) => void;
  onCancel: () => void;
  onNavigateToAssets: () => void;
}
export function EditRoomForm({
  roomData: initialRoomData,
  onSave,
  onCancel,
  onNavigateToAssets
}: EditRoomFormProps) {
  const {
    t
  } = useTranslation('rooms');
  const [roomData, setRoomData] = useState(initialRoomData);
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('info');
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleRemoveAmenity = (amenity: string) => {
    setRoomData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((a: string) => a !== amenity)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-8764">
      <div className="flex items-center" data-id="element-8765">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-8766">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-8767" />
          <span data-id="element-8768">{t('components.EditRoomForm.edit_room')}</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-8769">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-8770">
          <nav className="flex" role="tablist" aria-label="Room form sections" data-id="element-8771">
            <button role="tab" aria-selected={activeTab === 'info'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-8772">
              {t('components.EditRoomForm.room_information')}
            </button>
            <button role="tab" aria-selected={activeTab === 'media'} className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={onNavigateToAssets} data-id="element-8773">
              {t('components.EditRoomForm.media_assets')}
            </button>
          </nav>
        </div>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6" data-id="element-8774">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8775">
            <div data-id="element-8776">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8777">
                {t('components.AddRoomForm.room_name')}
                <span className="text-red-500" data-id="element-8778">*</span>
              </label>
              <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8779" />
            </div>
            <div data-id="element-8780">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8781">
                {t('components.AddRoomForm.room_category')}
                <span className="text-red-500" data-id="element-8782">*</span>
              </label>
              <div className="relative" data-id="element-8783">
                <div className="flex items-center" data-id="element-8784">
                  <input type="text" value={roomData.category} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-8785" />

                  <button type="button" aria-label="Clear category" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  category: ''
                }))} data-id="element-8786">
                    <XIcon size={16} aria-hidden="true" data-id="element-8787" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-8788">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8789">
              {t('components.AddRoomForm.room_code')}
            </label>
            <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8790" />
          </div>
          <div className="mb-6" data-id="element-8791">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8792">
              {t('components.AddRoomForm.description')}
            </label>
            <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8793" />
          </div>
          <div className="flex flex-wrap gap-6 mb-6" data-id="element-8794">
            <div className="flex items-center" data-id="element-8795">
              <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8796" />

              <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-8797">
                {t('components.AddRoomForm.smoking')}
              </label>
            </div>
            <div className="flex items-center" data-id="element-8798">
              <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8799" />

              <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-8800">
                {t('components.AddRoomForm.accessibility')}
              </label>
            </div>
            <div className="flex items-center" data-id="element-8801">
              <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8802" />

              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-8803">
                {t('components.AddRoomForm.status')}
              </label>
            </div>
          </div>
          {/* Room View and Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-8804">
            <div data-id="element-8805">
              <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8806">
                {t('components.AddRoomForm.room_view')}
                <span className="text-red-500" data-id="element-8807">*</span>
              </label>
              <div className="relative" data-id="element-8808">
                <div className="flex items-center" data-id="element-8809">
                  <input type="text" value={roomData.view} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-8810" />

                  <button type="button" aria-label="Clear view" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  view: ''
                }))} data-id="element-8811">
                    <XIcon size={16} aria-hidden="true" data-id="element-8812" />
                  </button>
                </div>
              </div>
            </div>
            <div data-id="element-8813">
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8814">
                {t('components.AddRoomForm.room_size_sq_m')}
              </label>
              <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8815" />
            </div>
          </div>
          {/* Room Amenities */}
          <div className="mb-6" data-id="element-8816">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-8817">
              {t('components.AddRoomForm.room_amenities')}
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-8818">
              {roomData.amenities.map((amenity: string) => <div key={amenity} className="flex items-center bg-gray-100 rounded-md px-2 py-1" data-id="element-8819">
                  <span className="text-sm" data-id="element-8820">{amenity}</span>
                  <button type="button" aria-label={`Remove ${amenity}`} onClick={() => handleRemoveAmenity(amenity)} className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-8821">
                    <XIcon size={12} aria-hidden="true" data-id="element-8822" />
                  </button>
                </div>)}
            </div>
          </div>
          {/* Occupancy Settings */}
          <div className="mb-6" data-id="element-8823">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8824">
              {t('components.AddRoomForm.occupancy_settings')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-8825">
              <div data-id="element-8826">
                <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8827">
                  {t('components.AddRoomForm.base_adults_occupancy')}
                  <span className="text-red-500" data-id="element-8828">*</span>
                </label>
                <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8829" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-8830">
                  {t('components.AddRoomForm.standard_number_of_allowed_adults_for')}
                </p>
              </div>
              <div data-id="element-8831">
                <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8832">
                  {t('components.AddRoomForm.base_children_occupancy')}
                  <span className="text-red-500" data-id="element-8833">*</span>
                </label>
                <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8834" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-8835">
                  {t('components.AddRoomForm.standard_number_of_allowed_children_for')}
                </p>
              </div>
              <div data-id="element-8836">
                <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8837">
                  {t('components.AddRoomForm.maximum_occupancy')}
                  <span className="text-red-500" data-id="element-8838">*</span>
                </label>
                <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8839" />

                <p className="text-xs text-gray-500 mt-1" data-id="element-8840">
                  {t('components.AddRoomForm.maximum_number_of_guests_allowed')}
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-4" data-id="element-8841">
              <div className="flex items-center" data-id="element-8842">
                <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8843" />

                <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-8844">
                  {t('components.AddRoomForm.allow_additional_adults')}
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-8845">
                {t('components.AddRoomForm.allow_additional_adults_not_exceeding_total')}
              </p>
              <div className="flex items-center mt-2" data-id="element-8846">
                <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-8847" />

                <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-8848">
                  {t('components.AddRoomForm.allow_additional_children')}
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-8849">
                {t('components.AddRoomForm.allow_additional_children_not_exceeding_total')}
              </p>
            </div>
            {/* Occupancy Summary */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-8850">
              <div className="flex" data-id="element-8851">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-8852" />
                <div data-id="element-8853">
                  <p className="text-sm text-red-700" data-id="element-8854">
                    {t('components.AddRoomForm.this_room_has_a_base_occupancy')}{' '}
                    {roomData.baseAdultsOccupancy}{' '}
                    {t('components.AddRoomForm.adults_and')}{' '}
                    {roomData.baseChildrenOccupancy}{' '}
                    {t('components.EditRoomForm.children_with_a_total_maximum_occupancy')}{' '}
                    {roomData.maxOccupancy}.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-8855">
                    {t('components.AddRoomForm.additional_adults_are_not_allowed')}
                  </p>
                  <p className="text-sm text-red-700" data-id="element-8856">
                    {t('components.AddRoomForm.additional_children_are_not_allowed')}
                  </p>
                  <p className="text-sm text-red-700" data-id="element-8857">
                    {t('components.EditRoomForm.additional_cost_per_child_or_adults')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Bed Configuration */}
          <div className="mb-6" data-id="element-8858">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-8859">
              {t('components.AddRoomForm.bed_configuration')}
              <span className="text-red-500" data-id="element-8860">*</span>
            </h3>
            {roomData.bedConfigurations.map((config: any, index: number) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-8861">
                <div data-id="element-8862">
                  <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8863">
                    {t('components.AddRoomForm.bed_type')}
                  </label>
                  <div className="relative" data-id="element-8864">
                    <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-8865">
                      <option value="" data-id="element-8866">
                        {t('components.AddRoomForm.select_bed_type')}
                      </option>
                      {bedTypes.map(type => <option key={type} value={type} data-id="element-8867">
                          {type}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-8868">
                      <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-8869">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-8870" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-8871">
                  <div className="flex-grow" data-id="element-8872">
                    <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-8873">
                      {t('components.AddRoomForm.bed_count')}
                    </label>
                    <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-8874" />
                  </div>
                  {roomData.bedConfigurations.length > 1 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-8875">
                      <TrashIcon size={18} data-id="element-8876" />
                    </button>}
                </div>
              </div>)}
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-8877">
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-8878">
              {t('components.AddRoomForm.save')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}